import type { ArmSummary, LlmModel, LlmUsageGroup, RecognitionArm } from '@/types/domain'
import { STAGE_TITLES } from '@/types/domain'

/**
 * The numbers this tab shows, and the rules for not lying with them.
 *
 * Every figure here is an *estimate at list prices* — the gateway's price
 * table is hand-maintained (`internal/models/llm_pricing.go`, Anthropic list
 * prices as of 2026-06-24) and knows nothing about an enterprise discount,
 * the Batch API's 50 % or a partner rate. The screen says so wherever a
 * figure appears; these helpers only make sure the figure itself is honest.
 */

const usd = (fractionDigits: number) =>
  new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })

/**
 * A dollar amount at the precision it is worth reading.
 *
 * One page pass costs a fraction of a cent, and rounding that to "0,00 $"
 * makes the whole table useless; a month of them costs dollars, where four
 * decimals are noise. Below a cent the figure keeps four decimals, above it
 * two.
 */
export function formatUsd(amount: number): string {
  if (!Number.isFinite(amount) || amount <= 0) return usd(2).format(0)
  if (amount < 0.01) return usd(4).format(amount)
  return usd(2).format(amount)
}

/**
 * What a cost figure reads as when the model is not in the price table.
 *
 * `priced: false` comes back with zero amounts, and rendering that zero
 * would tell someone a run was free. It was not; nobody knows what it cost.
 */
export const COST_UNKNOWN = 'unbekannt'

export function formatCost(amount: number, priced: boolean): string {
  return priced ? formatUsd(amount) : COST_UNKNOWN
}

const decimal = new Intl.NumberFormat('de-DE')

/** 1240000 → "1.240.000". */
export function formatCount(value: number): string {
  return decimal.format(Math.round(value))
}

/**
 * German labels for the `purpose` the worker records
 * (`PURPOSE` in `src/core/llmpass.py`, `recordcheck.py`, `fielddetect.py`;
 * the recognition arms reuse the first two rather than inventing their own).
 *
 * An unknown purpose is shown exactly as it was recorded rather than hidden:
 * a new operation belongs in the cost table the day it ships, not the day
 * somebody writes it a label.
 */
const PURPOSE_LABELS: Record<string, string> = {
  page_pass: 'KI-Seitenprüfung',
  record_check: 'Datensatzprüfung',
  field_detect: 'Felderkennung',
}

export function purposeLabel(purpose: string): string {
  return PURPOSE_LABELS[purpose] ?? (purpose.trim() === '' ? 'Unbekannt' : purpose)
}

/** What one call of this kind cost on average — the figure behind "what
 *  would 2000 Dokumente kosten?". Zero calls has no average. */
export function costPerCall(group: LlmUsageGroup): number | null {
  if (group.calls <= 0) return null
  return group.cost.totalUsd / group.calls
}

/**
 * Measured on this template, roughly 4400 input and 1500 output tokens go
 * into one page pass — the figures the Flutter picker was calibrated on.
 * Used only to put a comparable number next to a model that has never run
 * here; a model that *has* run is costed from its own `llm_calls` rows.
 */
export const TOKENS_PER_PAGE = { input: 4400, output: 1500 } as const

/** What 1000 pages would cost at a model's list rates. Rates are per
 *  million tokens. */
export function costPerThousandPages(model: LlmModel): number {
  const { Input, Output } = model.rates
  return (TOKENS_PER_PAGE.input * Input + TOKENS_PER_PAGE.output * Output) / 1000
}

/** The stage ladder's own titles for an arm, with the raw value kept for
 *  anything the gateway grows later. */
export function armLabel(arm: RecognitionArm | string): string {
  return STAGE_TITLES[arm as RecognitionArm] ?? arm
}

/**
 * The mark in front of a figure that is an upper bound rather than a bill.
 *
 * `costShared` says another arm on the same model may be billed for the same
 * calls — the page-pass family (`llm_image`, `llm_only`, `llm_web`) shares
 * one `purpose` in the audit trail, so its tokens and cost cannot be split
 * between them. Printing the figure bare would let someone add two arms
 * together and double-count the same run.
 */
export function costBound(arm: Pick<ArmSummary, 'costShared'>): string {
  return arm.costShared ? '≤ ' : ''
}

/**
 * Whether adopting this reading is offered.
 *
 * `human` is refused by the gateway (400 `arm_not_promotable`) — reviewed
 * values are written by reviewing — and a reading that is already delivered
 * would change nothing, so the button would be a no-op dressed as a
 * decision.
 */
export function isPromotable(arm: ArmSummary): boolean {
  return arm.arm !== 'human' && !arm.delivered && arm.documents > 0
}

/** "12.03. 09:41" — the date and the minute, which is what someone matches
 *  a call against a run. The full ISO stamp is unreadable in a table cell. */
export function shortTime(iso: string | undefined): string {
  if (!iso) return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const two = (n: number) => String(n).padStart(2, '0')
  return `${two(at.getDate())}.${two(at.getMonth() + 1)}. ${two(at.getHours())}:${two(at.getMinutes())}`
}

/** A consent date as stored (an ISO date, or whatever the approver typed).
 *  An unparseable value is shown verbatim rather than as "Invalid Date". */
export function consentDate(value: string | undefined): string {
  if (!value) return ''
  const at = new Date(value)
  if (Number.isNaN(at.getTime())) return value
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'long' }).format(at)
}
